import {
  type PaymentPeriod,
  type Plan,
  SubscriptionAction,
} from '@otter-ai/recall-client';
import type { CheckoutEntryPoint, PremiumFeature } from '@otter-ai/telemetry';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useExperimentEnabled } from '@/experiment/experiments-client';
import { recommendedSubscriptionActionQueryCreator } from '@/query/recommendedSubscriptionActionQueryCreator';
import { subscriptionQuery } from '@/query/subscriptionQueryCreator';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { openCCTrialDialog } from '@/shared/modals/cc-trial-modal/openCCTrialDialog';
import type { Subscription } from '@/shared/types/common';
import { OtterPlanType } from '@/shared/types/plan';
import { getJotaiStore } from '@/shared/utils/jotaiStore';
import { checkoutTrackingAtom } from '../../cc-trial-modal/utils/atoms';
import { openDialog } from '../../openDialog';
import { CheckoutDialogWrapper } from '../CheckoutDialogWrapper';
import { extractLocationFromSettings } from '../utils';

/**
 * Validates location for one-click checkout eligibility.
 * - US/CA: requires a zip/postal code
 * - Other countries: only requires a country code
 */
export function hasValidCheckoutLocation(
  countryCode: string | undefined,
  postalCode: string | undefined,
): boolean {
  if (!countryCode) return false;

  // US and CA require a postal code
  if (countryCode === 'US' || countryCode === 'CA') {
    return !!postalCode;
  }

  // Other countries only need a country code
  return true;
}

type SubscriptionHistoryItem = {
  plan_end_time: number;
  plan?: {
    type?: string;
    cycle?: 'month' | 'year';
  };
};

export type CheckoutParams = {
  planType: Plan['type'];
  cycle: PaymentPeriod;
  checkoutType: 'upgrade' | 'reactivate';
};

export type CheckoutDialogType = 'none' | 'one-click-checkout' | 'cc-trials';

export type OneClickCheckoutConfig = {
  dialogType: CheckoutDialogType;
  paramsToUse: CheckoutParams;
  showReactivateText: boolean;
};

export type TryOpenPaywallDialogParams = {
  config: OneClickCheckoutConfig;
  premiumFeature?: PremiumFeature | null;
  checkoutEntryPoint?: CheckoutEntryPoint | null;
};

function isFreeAccount(planType: string | undefined): boolean {
  return planType === OtterPlanType.FREE;
}

function hasSubscriptionHistory(history: unknown): boolean {
  return Array.isArray(history) && history.length > 0;
}

function getPreviousPlan(
  history: unknown,
): SubscriptionHistoryItem['plan'] | null {
  if (!Array.isArray(history) || history.length === 0) return null;

  const sortedHistory = [...(history as SubscriptionHistoryItem[])].sort(
    (a, b) => b.plan_end_time - a.plan_end_time,
  );

  return sortedHistory[0]?.plan ?? null;
}

function isCanceledPaidUser(
  planType: string | undefined,
  subscriptionHistory: unknown,
): boolean {
  if (!isFreeAccount(planType)) return false;
  if (!hasSubscriptionHistory(subscriptionHistory)) return false;

  const previousPlan = getPreviousPlan(subscriptionHistory);
  return (
    previousPlan?.type === OtterPlanType.TEAM ||
    previousPlan?.type === OtterPlanType.PREMIUM
  );
}

function getCheckoutParams(
  subscription: Subscription | null | undefined,
  defaultPlanType: Plan['type'],
  reactivateBusinessOnly: boolean,
): CheckoutParams {
  if (
    isCanceledPaidUser(
      subscription?.plan_type,
      subscription?.subscription_history,
    )
  ) {
    const previousPlan = getPreviousPlan(subscription?.subscription_history);

    const shouldReactivate = reactivateBusinessOnly
      ? previousPlan?.type === OtterPlanType.TEAM
      : true;

    if (shouldReactivate) {
      return {
        planType: (previousPlan?.type as Plan['type']) ?? defaultPlanType,
        cycle: (previousPlan?.cycle as PaymentPeriod) ?? 'year',
        checkoutType: 'reactivate',
      };
    }
  }

  return {
    planType: defaultPlanType,
    cycle: subscription?.plan?.cycle ?? 'year',
    checkoutType: 'upgrade',
  };
}

export type UsePaywallActionsResult = {
  isLoading: boolean;
  isRecommendedActionLoading: boolean;
  canOneClickCheckout: boolean;
  getOneClickCheckoutConfig: (
    defaultPlanType: Plan['type'],
    reactivateBusinessOnly?: boolean,
  ) => OneClickCheckoutConfig;
  tryOpenCheckoutDialog: (params: CheckoutParams) => boolean;
  tryOpenPaywallDialog: (params: TryOpenPaywallDialogParams) => boolean;
};

export function usePaywallActions(): UsePaywallActionsResult {
  const { data: subscription, isLoading } = useQuery(subscriptionQuery());

  const isCCTrialsEnabled = useExperimentEnabled('cc_trials_post_onboarding');
  const isReactivationEnabled = useExperimentEnabled(
    'one_click_reactivation_paywalls',
  );
  const isUpgradeEnabled = useExperimentEnabled('one_click_upgrade_paywalls');

  const { workspace, isLoading: isWorkspaceLoading } = useWorkspace();

  const { data: recommendedActionData, isLoading: isRecommendedActionLoading } =
    useQuery(
      recommendedSubscriptionActionQueryCreator(workspace?.id ?? 0, {
        // We only use this endpoint for CC trials post onboarding for now
        enabled: isCCTrialsEnabled && !isWorkspaceLoading && !!workspace?.id,
      }),
    );

  const location = extractLocationFromSettings(workspace?.settings);
  const hasValidLocation = hasValidCheckoutLocation(
    location?.country,
    location?.postal_code,
  );

  const canOneClickCheckout = useMemo(
    () =>
      hasValidLocation &&
      !!subscription?.payment_methods?.find(
        (payment) => payment.is_default && !payment.expired,
      ),
    [subscription, hasValidLocation],
  );

  const getOneClickCheckoutConfig = useMemo(() => {
    return (
      defaultPlanType: Plan['type'],
      reactivateBusinessOnly = false,
    ): OneClickCheckoutConfig => {
      const checkoutParams = getCheckoutParams(
        subscription,
        defaultPlanType,
        reactivateBusinessOnly,
      );
      const isReactivation = checkoutParams.checkoutType === 'reactivate';

      // CC Trials experiment: show CC trials dialog for post-onboarding users
      if (
        isCCTrialsEnabled &&
        recommendedActionData?.action === SubscriptionAction.CC_TRIAL
      ) {
        return {
          dialogType: 'cc-trials' as const,
          paramsToUse: checkoutParams,
          showReactivateText: false,
        };
      }

      if (isReactivation && isReactivationEnabled) {
        return {
          dialogType: 'one-click-checkout' as const,
          paramsToUse: checkoutParams,
          showReactivateText: true,
        };
      }
      if (isUpgradeEnabled) {
        return {
          dialogType: 'one-click-checkout' as const,
          paramsToUse: { ...checkoutParams, checkoutType: 'upgrade' },
          showReactivateText: false,
        };
      }

      return {
        dialogType: 'none' as const,
        paramsToUse: checkoutParams,
        showReactivateText: false,
      };
    };
  }, [
    subscription,
    isCCTrialsEnabled,
    isReactivationEnabled,
    isUpgradeEnabled,
    recommendedActionData,
  ]);

  const tryOpenCheckoutDialog = useCallback(
    (params: CheckoutParams): boolean => {
      if (!canOneClickCheckout) return false;

      openDialog(async ({ done }) => (
        <CheckoutDialogWrapper
          done={done}
          planType={params.planType}
          isEdu={false}
          cycle={params.cycle}
          checkoutType={params.checkoutType}
        />
      ));
      return true;
    },
    [canOneClickCheckout],
  );

  const tryOpenPaywallDialog = useCallback(
    ({
      config,
      premiumFeature,
      checkoutEntryPoint,
    }: TryOpenPaywallDialogParams): boolean => {
      const store = getJotaiStore();
      store.set(checkoutTrackingAtom, {
        premiumFeature: premiumFeature ?? null,
        checkoutEntryPoint: checkoutEntryPoint ?? null,
      });

      if (config.dialogType === 'cc-trials') {
        openCCTrialDialog({
          targetPlanType: config.paramsToUse.planType,
          premiumFeature,
          checkoutEntryPoint,
        });
        return true;
      }

      if (config.dialogType === 'one-click-checkout') {
        return tryOpenCheckoutDialog(config.paramsToUse);
      }

      return false;
    },
    [tryOpenCheckoutDialog],
  );

  return {
    isLoading,
    isRecommendedActionLoading,
    canOneClickCheckout,
    getOneClickCheckoutConfig,
    tryOpenCheckoutDialog,
    tryOpenPaywallDialog,
  };
}
